import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Paper,
  Divider,
  Stack,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add,
  Delete,
  Close,
  PhotoCamera,
  Phone,
  Badge as BadgeIcon,
  Person,
  DirectionsCar,
  PersonAddAlt,
} from "@mui/icons-material";
import provinces from "../../provinces.json";
import { Toaster } from "react-hot-toast";
import img from "../../images/profile_example.jpg";

const CAR_TYPES = ["รถกระบะ", "รถเก๋ง", "รถพวงข้าง", "รถ6ล้อ", "รถ10ล้อ"];

const Section = ({ title, children, action }) => (
  <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {action}
    </Stack>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

const PersonalInfoForm = ({
  readCard,
  formData,
  handleChange,
  customerGroups,
  group,
  setGroup,
  personType,
  setPersonType,
  cars = [],
  drivers = [],
  handleAddCar, // (car) => void   // เราจะเรียกตอนกดบันทึกใน Dialog
  handleAddDriver, // (driver) => void
  handleDeleteCar,
  handleDeleteDriver,
  handleCarChange,
  handleDriverChange,
  handleSubmit,
}) => {
  // ----- Dialog states -----
  const [carOpen, setCarOpen] = useState(false);
  const [driverOpen, setDriverOpen] = useState(false);

  // ฟอร์มชั่วคราวใน dialog
  const [newCar, setNewCar] = useState({ type: "", license: "", province: "" });
  const [newDriver, setNewDriver] = useState({
    name: "",
    surname: "",
    idCard: "",
  });

  // error states สำหรับ validate
  const [carErr, setCarErr] = useState({ type: "", license: "", province: "" });
  const [driverErr, setDriverErr] = useState({
    name: "",
    surname: "",
    idCard: "",
  });

  // อัปโหลดและพรีวิวรูปโปรไฟล์
  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      handleChange("img")({ target: { value: reader.result } });
    };
    reader.readAsDataURL(file);
  };

  // ----- Dialog handlers: Car -----
  const openCarDialog = () => {
    setNewCar({ type: "", license: "", province: "" });
    setCarErr({ type: "", license: "", province: "" });
    setCarOpen(true);
  };
  const closeCarDialog = () => setCarOpen(false);

  const saveCar = () => {
    // validate เบื้องต้น
    const nextErr = { type: "", license: "", province: "" };
    if (!newCar.type.trim()) nextErr.type = "กรุณาเลือกประเภทรถ";
    if (!newCar.license.trim()) nextErr.license = "กรุณากรอกทะเบียนรถ";
    if (!newCar.province.trim()) nextErr.province = "กรุณาเลือกจังหวัด";
    setCarErr(nextErr);

    const hasError = Object.values(nextErr).some(Boolean);
    if (hasError) return;

    // call parent handler
    handleAddCar({
      type: newCar.type,
      license: newCar.license,
      province: newCar.province,
    });
    setCarOpen(false);
  };

  // ----- Dialog handlers: Driver -----
  const openDriverDialog = () => {
    setNewDriver({ name: "", surname: "", idCard: "" });
    setDriverErr({ name: "", surname: "", idCard: "" });
    setDriverOpen(true);
  };
  const closeDriverDialog = () => setDriverOpen(false);

  const saveDriver = () => {
    const nextErr = { name: "", surname: "", idCard: "" };
    if (!newDriver.name.trim()) nextErr.name = "กรุณากรอกชื่อ";
    if (!newDriver.surname.trim()) nextErr.surname = "กรุณากรอกนามสกุล";
    if (!newDriver.idCard.trim()) nextErr.idCard = "กรุณากรอกเลขบัตรประชาชน";
    setDriverErr(nextErr);

    const hasError = Object.values(nextErr).some(Boolean);
    if (hasError) return;

    handleAddDriver({
      name: newDriver.name,
      surname: newDriver.surname,
      idCard: newDriver.idCard,
    });
    setDriverOpen(false);
  };

  const groupAdornment = useMemo(
    () =>
      group ? (
        <IconButton size="small" onClick={() => setGroup("")}>
          <Close fontSize="small" />
        </IconButton>
      ) : null,
    [group, setGroup]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: "auto" }}>
      <Toaster position="bottom-right" reverseOrder={false} />

      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            ข้อมูลซัพพลายเออร์
          </Typography>
          <Typography variant="body2" color="text.secondary">
            กรอกข้อมูลให้ครบถ้วน
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" onClick={readCard}>
            ดึงข้อมูลบัตรประชาชน
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            บันทึกข้อมูล
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* โปรไฟล์ + ประเภทบุคคล */}
        <Grid item xs={12} md={4}>
          <Section
            title="โปรไฟล์"
            action={
              <label htmlFor="profile-upload">
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleProfileImageChange}
                />
                <Tooltip title="อัปโหลดรูปโปรไฟล์">
                  <IconButton component="span" size="small">
                    <PhotoCamera />
                  </IconButton>
                </Tooltip>
              </label>
            }
          >
            <Box sx={{ display: "grid", placeItems: "center", mb: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  width: 200,
                  height: 200,
                  borderRadius: "999px",
                  overflow: "hidden",
                  boxShadow: 1,
                }}
              >
                <img
                  src={formData.img == null ? img : formData.img}
                  alt=""
                  style={{
                    width: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            </Box>

            <FormLabel sx={{ mb: 1 }}>ประเภทบุคคล</FormLabel>
            <RadioGroup
              row
              value={personType}
              onChange={(e) => setPersonType(e.target.value)}
            >
              <FormControlLabel
                value="normal"
                control={<Radio />}
                label="บุคคลธรรมดา"
              />
              <FormControlLabel
                value="corporate"
                control={<Radio />}
                label="นิติบุคคล"
              />
            </RadioGroup>
          </Section>
        </Grid>

        <Grid item xs={12} md={8}>
          <Section title="รายละเอียดซัพพลายเออร์">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ชื่อ-นามสกุล"
                  required
                  value={formData.name || ""}
                  onChange={handleChange("name")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {personType !== "corporate" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="date"
                    fullWidth
                    label="วันเดือนปีเกิด"
                    InputLabelProps={{ shrink: true }}
                    value={formData.dob || ""}
                    onChange={handleChange("dob")}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={
                    personType !== "corporate"
                      ? "เลขบัตรประชาชน"
                      : "เลขประจำตัวผู้เสียภาษี"
                  }
                  value={formData.citizenId || ""}
                  onChange={handleChange("citizenId")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="เบอร์โทรศัพท์"
                  value={formData.phone || ""}
                  onChange={handleChange("phone")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>กลุ่ม</InputLabel>
                  <Select
                    value={group}
                    label="กลุ่ม"
                    onChange={(e) => setGroup(e.target.value)}
                    endAdornment={
                      group && (
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => setGroup("")}
                          sx={{ mr: 1 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    {customerGroups.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ที่อยู่"
                  multiline
                  minRows={3}
                  value={formData.address || ""}
                  onChange={handleChange("address")}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* รถขนส่ง */}
        <Grid item xs={12}>
          <Section
            title="รถขนส่ง"
            action={
              <Button startIcon={<Add />} size="small" onClick={openCarDialog}>
                เพิ่มรถ
              </Button>
            }
          >
            {cars.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                ยังไม่มีรถในรายการ กด “เพิ่มรถ” ได้เลย
              </Typography>
            ) : (
              <Stack spacing={2}>
                {cars.map((car, index) => (
                  <Paper
                    key={`${car.license}-${index}`}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, borderStyle: "dashed" }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>ประเภทรถ</InputLabel>
                          <Select
                            value={car.type || ""}
                            label="ประเภทรถ"
                            onChange={(e) =>
                              handleCarChange(index, "type", e.target.value)
                            }
                            startAdornment={
                              <InputAdornment position="start" sx={{ pl: 1 }}>
                                <DirectionsCar fontSize="small" />
                              </InputAdornment>
                            }
                          >
                            {CAR_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="ทะเบียนรถ"
                          value={car.license || ""}
                          onChange={(e) =>
                            handleCarChange(index, "license", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>จังหวัด</InputLabel>
                          <Select
                            value={car.province || ""}
                            label="จังหวัด"
                            onChange={(e) =>
                              handleCarChange(index, "province", e.target.value)
                            }
                          >
                            {provinces.map((name) => (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                        display="flex"
                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      >
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteCar(index)}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </Section>
        </Grid>

        {/* คนขับ */}
        <Grid item xs={12}>
          <Section
            title="คนขับ"
            action={
              <Button
                startIcon={<PersonAddAlt />}
                size="small"
                onClick={openDriverDialog}
              >
                เพิ่มคนขับ
              </Button>
            }
          >
            {drivers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                ยังไม่มีคนขับในรายการ กด “เพิ่มคนขับ” ได้เลย
              </Typography>
            ) : (
              <Stack spacing={2}>
                {drivers.map((d, index) => (
                  <Paper
                    key={`${d.idCard || index}-${index}`}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, borderStyle: "dashed" }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="ชื่อคนขับ"
                          value={d.name || ""}
                          onChange={(e) =>
                            handleDriverChange(index, "name", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="นามสกุล"
                          value={d.surname || ""}
                          onChange={(e) =>
                            handleDriverChange(index, "surname", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="เลขบัตรประชาชน"
                          value={d.idCard || ""}
                          onChange={(e) =>
                            handleDriverChange(index, "idCard", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={1}
                        display="flex"
                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      >
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteDriver(index)}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            )}
          </Section>
        </Grid>
      </Grid>

      {/* ---------------- Dialog: เพิ่มรถ ---------------- */}
      <Dialog open={carOpen} onClose={closeCarDialog} fullWidth maxWidth="sm">
        <DialogTitle>เพิ่มรถขนส่ง</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!carErr.type}>
                <InputLabel>ประเภทรถ</InputLabel>
                <Select
                  value={newCar.type}
                  label="ประเภทรถ"
                  onChange={(e) =>
                    setNewCar((s) => ({ ...s, type: e.target.value }))
                  }
                >
                  {CAR_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {carErr.type && (
                <Typography color="error" variant="caption">
                  {carErr.type}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ทะเบียนรถ"
                value={newCar.license}
                onChange={(e) =>
                  setNewCar((s) => ({ ...s, license: e.target.value }))
                }
                error={!!carErr.license}
                helperText={carErr.license}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!carErr.province}>
                <InputLabel>จังหวัด</InputLabel>
                <Select
                  value={newCar.province}
                  label="จังหวัด"
                  onChange={(e) =>
                    setNewCar((s) => ({ ...s, province: e.target.value }))
                  }
                >
                  {provinces.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {carErr.province && (
                <Typography color="error" variant="caption">
                  {carErr.province}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCarDialog} startIcon={<Close />}>
            ยกเลิก
          </Button>
          <Button variant="contained" onClick={saveCar} startIcon={<Add />}>
            เพิ่มรถ
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Dialog: เพิ่มคนขับ ---------------- */}
      <Dialog
        open={driverOpen}
        onClose={closeDriverDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>เพิ่มคนขับ</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ชื่อ"
                value={newDriver.name}
                onChange={(e) =>
                  setNewDriver((s) => ({ ...s, name: e.target.value }))
                }
                error={!!driverErr.name}
                helperText={driverErr.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="นามสกุล"
                value={newDriver.surname}
                onChange={(e) =>
                  setNewDriver((s) => ({ ...s, surname: e.target.value }))
                }
                error={!!driverErr.surname}
                helperText={driverErr.surname}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="เลขบัตรประชาชน"
                value={newDriver.idCard}
                onChange={(e) =>
                  setNewDriver((s) => ({ ...s, idCard: e.target.value }))
                }
                error={!!driverErr.idCard}
                helperText={driverErr.idCard}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDriverDialog} startIcon={<Close />}>
            ยกเลิก
          </Button>
          <Button variant="contained" onClick={saveDriver} startIcon={<Add />}>
            เพิ่มคนขับ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonalInfoForm;
